import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";
import type { DateRange } from "react-day-picker";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  CreditCard,
  DollarSign,
  Download,
  LayoutGrid,
  Loader2,
  MoveHorizontal,
  Table2,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCashflowAR } from "@/hooks/useAccounting";
import {
  exportToCSV,
  formatCurrencyForExport,
  type ExportColumn,
} from "@/lib/export";
import { copyTextToClipboard } from "@/lib/clipboard";
import { formatCurrency, formatDate } from "@/lib/format";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CashFlowRow {
  id: string;
  orderId: string;
  orderNumber: string | null;
  userId: string | null;
  customerName: string | null;
  company: string | null;
  isEnterprise: boolean;
  amount: number;
  scheduledDate: number;
  executedAt: number | null;
  status: string;
  isOverdue: boolean;
  installmentNumber: number | null;
  totalInstallments: number | null;
  paymentMethod: string | null;
  // Timeline fields
  phase: "scheduled" | "stripe_processing" | "collections" | "completed";
  timelineDate: number; // The date this card appears on in the timeline
  estimatedReleaseDate: number | null;
  releaseSource: "stripe" | "estimated" | null;
  stripeStatusMessage: string | null;
  stripeProcessingFee: number | null;
  attemptCount: number | null;
  failureReason: string | null;
  lateFeeApplied: boolean;
  linkedPaymentMethodType: string | null;
  last4: string | null;
  brand: string | null;
  bankName: string | null;
}

// ---------------------------------------------------------------------------
// Date range presets
// ---------------------------------------------------------------------------

type RangePreset =
  | "this_month"
  | "next_month"
  | "this_quarter"
  | "next_30"
  | "next_60"
  | "next_90";

type TimelineFocusTarget = "today" | "start" | "end";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function getDateRange(preset: RangePreset): {
  start: number;
  end: number;
  label: string;
} {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (preset) {
    case "this_month": {
      const start = Date.UTC(year, month, 1);
      const end = Date.UTC(year, month + 1, 0, 23, 59, 59, 999);
      return {
        start,
        end,
        label: new Intl.DateTimeFormat("en-US", {
          month: "long",
          year: "numeric",
        }).format(new Date(start)),
      };
    }
    case "next_month": {
      const start = Date.UTC(year, month + 1, 1);
      const end = Date.UTC(year, month + 2, 0, 23, 59, 59, 999);
      return {
        start,
        end,
        label: new Intl.DateTimeFormat("en-US", {
          month: "long",
          year: "numeric",
        }).format(new Date(start)),
      };
    }
    case "this_quarter": {
      const qStart = Math.floor(month / 3) * 3;
      const start = Date.UTC(year, qStart, 1);
      const end = Date.UTC(year, qStart + 3, 0, 23, 59, 59, 999);
      return {
        start,
        end,
        label: `Q${Math.floor(qStart / 3) + 1} ${year}`,
      };
    }
    case "next_30": {
      const start = Date.UTC(year, month, now.getDate());
      const end = start + 30 * 24 * 60 * 60 * 1000 - 1;
      return { start, end, label: "Next 30 Days" };
    }
    case "next_60": {
      const start = Date.UTC(year, month, now.getDate());
      const end = start + 60 * 24 * 60 * 60 * 1000 - 1;
      return { start, end, label: "Next 60 Days" };
    }
    case "next_90": {
      const start = Date.UTC(year, month, now.getDate());
      const end = start + 90 * 24 * 60 * 60 * 1000 - 1;
      return { start, end, label: "Next 90 Days" };
    }
  }
}

function toUtcStartOfDay(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

function toUtcEndOfDay(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function toDayKeyFromCalendarDate(date: Date): string {
  return toDayKey(toUtcStartOfDay(date));
}

function buildCustomDateRange(range: DateRange): {
  start: number;
  end: number;
  label: string;
} | null {
  if (!range.from || !range.to) return null;

  return {
    start: toUtcStartOfDay(range.from),
    end: toUtcEndOfDay(range.to),
    label: `${formatDayKeyLabel(toDayKeyFromCalendarDate(range.from))} – ${formatDayKeyLabel(
      toDayKeyFromCalendarDate(range.to),
    )}`,
  };
}

function toCalendarDate(ms: number): Date {
  const [year, month, day] = parseDayKey(toDayKey(ms));
  return new Date(year, month - 1, day);
}

function shiftDateSelectionByDays(
  range: { start: number; end: number },
  days: number,
): DateRange {
  const delta = days * ONE_DAY_MS;
  return {
    from: toCalendarDate(range.start + delta),
    to: toCalendarDate(range.end + delta),
  };
}

function formatDateRangeTriggerLabel(range: DateRange | undefined): string {
  if (!range?.from && !range?.to) return "Custom Range";
  if (range.from && !range.to) {
    return `${formatDayKeyLabel(toDayKeyFromCalendarDate(range.from))} – Pick end`;
  }
  if (range.from && range.to) {
    return `${formatDayKeyLabel(toDayKeyFromCalendarDate(range.from))} – ${formatDayKeyLabel(
      toDayKeyFromCalendarDate(range.to),
    )}`;
  }
  return "Custom Range";
}

function isSingleDaySelection(range: DateRange): boolean {
  return !!(
    range.from &&
    range.to &&
    toDayKeyFromCalendarDate(range.from) === toDayKeyFromCalendarDate(range.to)
  );
}

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

const STATUS_BADGE_CLASSES: Record<string, string> = {
  pending:
    "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200",
  processing:
    "border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-200",
  completed:
    "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200",
  failed:
    "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200",
};

const OVERDUE_BADGE =
  "border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-800/60 dark:bg-orange-950/40 dark:text-orange-200";

function getStatusBadgeClass(status: string, isOverdue: boolean): string {
  if (isOverdue) return OVERDUE_BADGE;
  return (
    STATUS_BADGE_CLASSES[status] ??
    "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200"
  );
}

function getStatusLabel(status: string, isOverdue: boolean): string {
  if (isOverdue) return "Overdue";
  switch (status) {
    case "pending":
      return "Pending";
    case "processing":
      return "Processing";
    case "completed":
      return "Collected";
    case "failed":
      return "Failed";
    default:
      return status;
  }
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  scheduled: "Auto-Charge",
  cash_cheque: "Pay Manually Later",
  payment_link: "Payment Link",
};

// ---------------------------------------------------------------------------
// Lane configuration
// ---------------------------------------------------------------------------

interface LaneConfig {
  id: "scheduled" | "stripe_processing" | "collections";
  label: string;
  description: string;
  colorClass: string;
  headerBg: string;
  dotColor: string;
  icon: React.ElementType;
}

const LANES: LaneConfig[] = [
  {
    id: "scheduled",
    label: "Scheduled AR Automation",
    description: "Future payments pending auto-charge",
    colorClass: "text-yellow-700 dark:text-yellow-300",
    headerBg:
      "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800/40",
    dotColor: "bg-yellow-500",
    icon: Clock,
  },
  {
    id: "stripe_processing",
    label: "Pending Deposit",
    description: "Processing or awaiting bank deposit",
    colorClass: "text-blue-700 dark:text-blue-300",
    headerBg:
      "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800/40",
    dotColor: "bg-blue-500",
    icon: CreditCard,
  },
  {
    id: "collections",
    label: "AR in Collections",
    description: "Overdue or failed payments requiring action",
    colorClass: "text-red-700 dark:text-red-300",
    headerBg:
      "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800/40",
    dotColor: "bg-red-500",
    icon: AlertTriangle,
  },
];

// ---------------------------------------------------------------------------
// Day helpers
// ---------------------------------------------------------------------------

function toDayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

function parseDayKey(dayKey: string): [number, number, number] {
  const parts = dayKey.split("-");
  return [Number(parts[0]), Number(parts[1]), Number(parts[2])];
}

function dayOfMonth(dayKey: string): string {
  const [, , d] = parseDayKey(dayKey);
  return String(d);
}

function weekdayShort(dayKey: string): string {
  const [y, m, d] = parseDayKey(dayKey);
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    timeZone: "UTC",
  }).format(date);
}

function monthShort(dayKey: string): string {
  const [y, m, d] = parseDayKey(dayKey);
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    timeZone: "UTC",
  }).format(date);
}

function isMonthBoundary(dayKey: string, previousDayKey?: string): boolean {
  if (!previousDayKey) return true;
  const [year, month] = parseDayKey(dayKey);
  const [previousYear, previousMonth] = parseDayKey(previousDayKey);
  return year !== previousYear || month !== previousMonth;
}

function formatDayKeyLabel(dayKey: string): string {
  const [y, m, d] = parseDayKey(dayKey);
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function isToday(dayKey: string): boolean {
  return dayKey === toDayKey(Date.now());
}

function isWeekend(dayKey: string): boolean {
  const [y, m, d] = parseDayKey(dayKey);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dow = date.getUTCDay();
  return dow === 0 || dow === 6;
}

/** Generate all day keys between start and end (inclusive). */
function generateDayKeys(startMs: number, endMs: number): string[] {
  const keys: string[] = [];
  const cursor = new Date(startMs);
  cursor.setUTCHours(0, 0, 0, 0);
  const end = new Date(endMs);
  end.setUTCHours(23, 59, 59, 999);
  while (cursor <= end) {
    keys.push(toDayKey(cursor.getTime()));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return keys;
}

/** Group rows by day key for a given phase.
 *  Uses `timelineDate` for positioning — this is the deposit date for blue-lane
 *  items, the failure date for red-lane items, and the scheduled date for yellow. */
function groupByDay(
  rows: CashFlowRow[],
  phase: string,
): Map<string, CashFlowRow[]> {
  const map = new Map<string, CashFlowRow[]>();
  for (const row of rows) {
    if (row.phase !== phase) continue;
    const key = toDayKey(row.timelineDate);
    const arr = map.get(key);
    if (arr) {
      arr.push(row);
    } else {
      map.set(key, [row]);
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Payment method display
// ---------------------------------------------------------------------------

function formatPaymentMethodShort(row: CashFlowRow): string {
  if (row.linkedPaymentMethodType === "us_bank_account") {
    return row.bankName
      ? `ACH ${row.bankName}${row.last4 ? ` ****${row.last4}` : ""}`
      : `ACH${row.last4 ? ` ****${row.last4}` : ""}`;
  }
  if (row.brand && row.last4) {
    return `${row.brand} ****${row.last4}`;
  }
  if (row.last4) return `****${row.last4}`;
  if (row.paymentMethod) return PAYMENT_METHOD_LABELS[row.paymentMethod] ?? row.paymentMethod;
  return "-";
}

// ---------------------------------------------------------------------------
// Timeline payment card
// ---------------------------------------------------------------------------

function TimelinePaymentCard({ row }: { row: CashFlowRow }) {
  const isCollections = row.phase === "collections";
  const isProcessing = row.phase === "stripe_processing";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex w-full min-w-0 cursor-default flex-col gap-1 rounded-md border p-2 text-xs transition-colors hover:bg-accent/50",
              isCollections &&
                "border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-950/20",
              isProcessing &&
                "border-blue-200 bg-blue-50/50 dark:border-blue-900/40 dark:bg-blue-950/20",
              !isCollections &&
                !isProcessing &&
                "border-border bg-card",
            )}
          >
            {/* Header: amount + order */}
            <div className="flex items-center justify-between gap-1">
              <span className="truncate font-mono font-semibold">
                {formatCurrency(row.amount)}
              </span>
              {row.installmentNumber && row.totalInstallments && (
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {row.installmentNumber}/{row.totalInstallments}
                </span>
              )}
            </div>

            {/* Customer */}
            <span className="truncate text-muted-foreground">
              {row.company || row.customerName || "Unknown"}
            </span>

            {/* Order # */}
            <span className="truncate font-mono text-[10px] text-muted-foreground">
              #{row.orderNumber ?? row.orderId.slice(-8)}
            </span>

            {/* Stripe status message for processing / failure */}
            {row.stripeStatusMessage && (
              <span
                className={cn(
                  "truncate text-[10px] font-medium",
                  isCollections
                    ? "text-red-600 dark:text-red-400"
                    : "text-blue-600 dark:text-blue-400",
                )}
              >
                {row.stripeStatusMessage}
              </span>
            )}

            {/* Deposit / release date */}
            {isProcessing && row.estimatedReleaseDate && (
              <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">
                {row.releaseSource === "stripe" ? "Deposit" : "Est. deposit"}:{" "}
                {formatDate(row.estimatedReleaseDate, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
            {/* Show original due date as context when card has moved to deposit date */}
            {isProcessing && row.timelineDate !== row.scheduledDate && (
              <span className="text-[10px] text-muted-foreground">
                Due:{" "}
                {formatDate(row.scheduledDate, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}

            {/* Collection badges */}
            {isCollections && (
              <div className="flex flex-wrap gap-1">
                {row.isOverdue && (
                  <Badge variant="outline" className={cn(OVERDUE_BADGE, "text-[10px] px-1 py-0")}>
                    Overdue
                  </Badge>
                )}
                {row.status === "failed" && (
                  <Badge
                    variant="outline"
                    className={cn(STATUS_BADGE_CLASSES.failed, "text-[10px] px-1 py-0")}
                  >
                    Failed
                  </Badge>
                )}
                {row.lateFeeApplied && (
                  <Badge
                    variant="outline"
                    className="border-orange-300 bg-orange-100 text-[10px] text-orange-800 px-1 py-0 dark:border-orange-800/60 dark:bg-orange-950/40 dark:text-orange-300"
                  >
                    Late Fee
                  </Badge>
                )}
                {row.attemptCount != null && row.attemptCount > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {row.attemptCount} attempt{row.attemptCount !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs space-y-1">
          <p className="font-semibold">
            {formatCurrency(row.amount)} — {row.company || row.customerName || "Unknown"}
          </p>
          <p>
            Order #{row.orderNumber ?? row.orderId.slice(-8)}
            {row.installmentNumber && row.totalInstallments
              ? ` (installment ${row.installmentNumber} of ${row.totalInstallments})`
              : ""}
          </p>
          <p>
            Due: {formatDate(row.scheduledDate, { month: "short", day: "numeric", year: "numeric" })}
          </p>
          <p>Method: {formatPaymentMethodShort(row)}</p>
          {row.stripeStatusMessage && <p>Stripe: {row.stripeStatusMessage}</p>}
          {row.estimatedReleaseDate && (
            <p>
              {row.releaseSource === "stripe" ? "Deposit date" : "Est. deposit"}:{" "}
              {formatDate(row.estimatedReleaseDate, { month: "short", day: "numeric", year: "numeric" })}
            </p>
          )}
          {row.stripeProcessingFee != null && row.stripeProcessingFee > 0 && (
            <p>Stripe fee: {formatCurrency(row.stripeProcessingFee)}</p>
          )}
          {row.failureReason && <p className="text-red-400">Failure: {row.failureReason}</p>}
          {row.isEnterprise && <p>Enterprise account</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const DAY_GROUP_STYLES: Record<
  LaneConfig["id"],
  { shell: string; header: string }
> = {
  scheduled: {
    shell:
      "border-yellow-200/80 bg-background/95 shadow-xs dark:border-yellow-900/40",
    header:
      "border-yellow-200/70 bg-yellow-50/80 dark:border-yellow-900/40 dark:bg-yellow-950/20",
  },
  stripe_processing: {
    shell:
      "border-blue-200/80 bg-background/95 shadow-xs dark:border-blue-900/40",
    header:
      "border-blue-200/70 bg-blue-50/80 dark:border-blue-900/40 dark:bg-blue-950/20",
  },
  collections: {
    shell:
      "border-red-200/80 bg-background/95 shadow-xs dark:border-red-900/40",
    header:
      "border-red-200/70 bg-red-50/80 dark:border-red-900/40 dark:bg-red-950/20",
  },
};

function TimelineDayGroup({
  lane,
  rows,
}: {
  lane: LaneConfig;
  rows: CashFlowRow[];
}) {
  const [copied, setCopied] = React.useState(false);
  const resetCopiedRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const total = React.useMemo(
    () => rows.reduce((sum, row) => sum + row.amount, 0),
    [rows],
  );
  const formattedTotal = React.useMemo(() => formatCurrency(total), [total]);
  const groupStyles = DAY_GROUP_STYLES[lane.id];

  React.useEffect(
    () => () => {
      if (resetCopiedRef.current) {
        clearTimeout(resetCopiedRef.current);
      }
    },
    [],
  );

  const handleCopyTotal = React.useCallback(async () => {
    try {
      await copyTextToClipboard(formattedTotal);
      setCopied(true);
      if (resetCopiedRef.current) {
        clearTimeout(resetCopiedRef.current);
      }
      resetCopiedRef.current = setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to copy total",
      );
    }
  }, [formattedTotal]);

  return (
    <div className={cn("overflow-hidden rounded-xl border", groupStyles.shell)}>
      <button
        type="button"
        onClick={() => void handleCopyTotal()}
        className={cn(
          "flex w-full flex-col items-start gap-1 border-b px-3 py-2 text-left transition-colors hover:bg-accent/30",
          groupStyles.header,
        )}
        aria-label={`Copy daily total ${formattedTotal}`}
        title={`Copy ${formattedTotal}`}
      >
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Total
          </span>
          <span
            aria-live="polite"
            className={cn(
              "inline-flex items-center gap-1 text-[10px] font-medium",
              copied
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-muted-foreground",
            )}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </span>
        </div>
        <span className={cn("font-mono text-sm font-semibold leading-none", lane.colorClass)}>
          {formattedTotal}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {rows.length} payment{rows.length !== 1 ? "s" : ""}
        </span>
      </button>

      <div className="space-y-1.5 p-1.5">
        {rows.map((row) => (
          <TimelinePaymentCard key={row.id} row={row} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline view
// ---------------------------------------------------------------------------

const DAY_COL_WIDTH_FALLBACK = 140; // px per day column before layout is measured
const DAY_COL_MIN_WIDTH = 112; // px minimum day width on narrow screens
const TIMELINE_VISIBLE_DAYS = 7;
const LANE_LABEL_WIDTH = 200; // px for the lane label column
const TIMELINE_SCROLL_DAYS = TIMELINE_VISIBLE_DAYS;

function TimelineView({
  rows,
  startMs,
  endMs,
  focusTarget,
  onShiftRange,
}: {
  rows: CashFlowRow[];
  startMs: number;
  endMs: number;
  focusTarget: TimelineFocusTarget;
  onShiftRange: (days: number, focusTarget: TimelineFocusTarget) => void;
}) {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = React.useState(0);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [visibleWindow, setVisibleWindow] = React.useState({
    startIdx: 0,
    endIdx: 0,
  });

  const dayKeys = React.useMemo(() => generateDayKeys(startMs, endMs), [startMs, endMs]);
  const dayColWidth = React.useMemo(() => {
    if (!viewportWidth) return DAY_COL_WIDTH_FALLBACK;
    return Math.max(
      DAY_COL_MIN_WIDTH,
      Math.floor(viewportWidth / TIMELINE_VISIBLE_DAYS),
    );
  }, [viewportWidth]);
  const totalWidth = dayKeys.length * dayColWidth;

  // Group data by lane
  const laneData = React.useMemo(() => {
    const result = new Map<string, Map<string, CashFlowRow[]>>();
    for (const lane of LANES) {
      result.set(lane.id, groupByDay(rows, lane.id));
    }
    return result;
  }, [rows]);

  // Count per lane for the header
  const laneCounts = React.useMemo(() => {
    const counts = new Map<string, { count: number; total: number }>();
    for (const lane of LANES) {
      const phaseRows = rows.filter((r) => r.phase === lane.id);
      counts.set(lane.id, {
        count: phaseRows.length,
        total: phaseRows.reduce((s, r) => s + r.amount, 0),
      });
    }
    return counts;
  }, [rows]);

  const syncTimelineViewport = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || dayKeys.length === 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      setVisibleWindow({ startIdx: 0, endIdx: 0 });
      return;
    }

    const maxScrollLeft = Math.max(0, totalWidth - container.clientWidth);
    const scrollLeft = container.scrollLeft;
    const startIdx = Math.max(0, Math.floor(scrollLeft / dayColWidth));
    const visibleDayCount = Math.max(1, Math.ceil(container.clientWidth / dayColWidth));
    const endIdx = Math.min(dayKeys.length - 1, startIdx + visibleDayCount - 1);

    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft < maxScrollLeft - 4);
    setVisibleWindow({ startIdx, endIdx });
  }, [dayColWidth, dayKeys.length, totalWidth]);

  const scrollTimelineTo = React.useCallback(
    (left: number) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const maxScrollLeft = Math.max(0, totalWidth - container.clientWidth);
      const nextLeft = Math.max(0, Math.min(left, maxScrollLeft));

      if (typeof container.scrollTo === "function") {
        container.scrollTo({ left: nextLeft, behavior: "smooth" });
      } else {
        container.scrollLeft = nextLeft;
      }

      requestAnimationFrame(syncTimelineViewport);
    },
    [syncTimelineViewport, totalWidth],
  );

  const scrollByDays = React.useCallback(
    (days: number) => {
      const container = scrollContainerRef.current;
      if (!container) return;
      scrollTimelineTo(container.scrollLeft + days * dayColWidth);
    },
    [dayColWidth, scrollTimelineTo],
  );

  const scrollToStart = React.useCallback(() => {
    scrollTimelineTo(0);
  }, [scrollTimelineTo]);

  const scrollToEnd = React.useCallback(() => {
    scrollTimelineTo(totalWidth);
  }, [scrollTimelineTo, totalWidth]);

  const scrollToToday = React.useCallback(() => {
    const todayIdx = dayKeys.findIndex(isToday);
    if (todayIdx < 0) {
      scrollToStart();
      return;
    }

    scrollTimelineTo(todayIdx * dayColWidth - dayColWidth * 2);
  }, [dayColWidth, dayKeys, scrollTimelineTo, scrollToStart]);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      syncTimelineViewport();
      return;
    }

    const updateMeasurements = () => {
      setViewportWidth(container.clientWidth);
      syncTimelineViewport();
    };

    updateMeasurements();

    const handleScroll = () => syncTimelineViewport();
    const handleResize = () => updateMeasurements();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updateMeasurements())
        : null;

    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    resizeObserver?.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();
    };
  }, [syncTimelineViewport]);

  React.useEffect(() => {
    if (focusTarget === "start") {
      scrollToStart();
      return;
    }
    if (focusTarget === "end") {
      scrollToEnd();
      return;
    }
    scrollToToday();
  }, [focusTarget, scrollToEnd, scrollToStart, scrollToToday]);

  const visibleRangeLabel = React.useMemo(() => {
    if (dayKeys.length === 0) return null;
    const startKey = dayKeys[Math.min(visibleWindow.startIdx, dayKeys.length - 1)]!;
    const endKey = dayKeys[Math.min(visibleWindow.endIdx, dayKeys.length - 1)]!;
    if (startKey === endKey) return formatDayKeyLabel(startKey);
    return `${formatDayKeyLabel(startKey)} – ${formatDayKeyLabel(endKey)}`;
  }, [dayKeys, visibleWindow.endIdx, visibleWindow.startIdx]);

  const handlePageLeft = React.useCallback(() => {
    if (canScrollLeft) {
      scrollByDays(-TIMELINE_SCROLL_DAYS);
      return;
    }
    onShiftRange(-TIMELINE_SCROLL_DAYS, "start");
  }, [canScrollLeft, onShiftRange, scrollByDays]);

  const handlePageRight = React.useCallback(() => {
    if (canScrollRight) {
      scrollByDays(TIMELINE_SCROLL_DAYS);
      return;
    }
    onShiftRange(TIMELINE_SCROLL_DAYS, "end");
  }, [canScrollRight, onShiftRange, scrollByDays]);

  const scrollToNearestLaneItem = React.useCallback(
    (laneId: LaneConfig["id"]) => {
      const dayMap = laneData.get(laneId);
      if (!dayMap || dayMap.size === 0) return;

      const targetIndices = Array.from(dayMap.keys())
        .map((dayKey) => dayKeys.indexOf(dayKey))
        .filter((index) => index >= 0);

      if (targetIndices.length === 0) return;

      const visibleCenter = (visibleWindow.startIdx + visibleWindow.endIdx) / 2;
      const targetIndex = targetIndices.reduce((closest, candidate) => {
        return Math.abs(candidate - visibleCenter) < Math.abs(closest - visibleCenter)
          ? candidate
          : closest;
      }, targetIndices[0]!);
      const targetDayKey = dayKeys[targetIndex];
      const container = scrollContainerRef.current;

      if (container && targetDayKey) {
        const targetCell = container.querySelector<HTMLElement>(
          `[data-day-key="${targetDayKey}"][data-lane-id="${laneId}"]`,
        );
        if (targetCell) {
          scrollTimelineTo(targetCell.offsetLeft - dayColWidth);
          return;
        }
      }

      scrollTimelineTo(targetIndex * dayColWidth - dayColWidth);
    },
    [dayColWidth, dayKeys, laneData, scrollTimelineTo, visibleWindow.endIdx, visibleWindow.startIdx],
  );

  // If no non-completed payments to show in lanes
  const hasLaneData = rows.some(
    (r) => r.phase === "scheduled" || r.phase === "stripe_processing" || r.phase === "collections",
  );

  if (!hasLaneData) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-md border border-dashed">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <CalendarDays className="h-8 w-8 opacity-40" />
          <p className="text-sm font-medium">No active AR items</p>
          <p className="text-xs">
            All payments in this range are completed
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/20 px-3 py-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-foreground">
            Viewing {visibleRangeLabel}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <MoveHorizontal className="h-3.5 w-3.5" />
            Showing about {TIMELINE_VISIBLE_DAYS} days at a time. The arrows keep paging by 7 days, even past the current month.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePageLeft}
            aria-label="Go back 7 days"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            7d
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => scrollToToday()}
            aria-label="Scroll timeline to today"
          >
            Today
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePageRight}
            aria-label="Go forward 7 days"
          >
            7d
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Timeline container with synced horizontal scroll */}
      <div className="flex">
        {/* Fixed lane label column */}
        <div
          className="shrink-0 border-r"
          style={{ width: LANE_LABEL_WIDTH }}
        >
          {/* Day header spacer */}
          <div className="h-[60px] border-b bg-muted/30" />

          {/* Lane labels */}
          {LANES.map((lane) => {
            const lc = laneCounts.get(lane.id);
            const Icon = lane.icon;
            return (
              <button
                key={lane.id}
                type="button"
                onClick={() => scrollToNearestLaneItem(lane.id)}
                disabled={!lc || lc.count === 0}
                className={cn(
                  "flex w-full flex-col justify-center border-b px-3 py-2 text-left transition-colors",
                  lane.headerBg,
                  lc && lc.count > 0
                    ? "cursor-pointer hover:bg-accent/40"
                    : "cursor-default",
                )}
                style={{ minHeight: 100 }}
                aria-label={
                  lc && lc.count > 0
                    ? `Jump to closest ${lane.label} payment`
                    : undefined
                }
                title={
                  lc && lc.count > 0
                    ? `Jump to closest ${lane.label} payment`
                    : undefined
                }
              >
                <div className="flex items-center gap-2">
                  <div className={cn("h-2 w-2 rounded-full", lane.dotColor)} />
                  <Icon className={cn("h-3.5 w-3.5", lane.colorClass)} />
                  <span className={cn("text-xs font-semibold", lane.colorClass)}>
                    {lane.label}
                  </span>
                </div>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  {lane.description}
                </p>
                {lc && lc.count > 0 && (
                  <p className="mt-1 text-[10px] font-medium text-muted-foreground">
                    {lc.count} payment{lc.count !== 1 ? "s" : ""} &middot;{" "}
                    {formatCurrency(lc.total)}
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Scrollable timeline area */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto"
        >
          <div style={{ minWidth: totalWidth }}>
            {/* Day header row */}
            <div className="sticky top-0 z-10 flex h-[60px] border-b bg-muted/30">
              {dayKeys.map((dk, index) => {
                const today = isToday(dk);
                const weekend = isWeekend(dk);
                const monthBoundary = isMonthBoundary(dk, dayKeys[index - 1]);
                return (
                  <div
                    key={dk}
                    className={cn(
                      "flex shrink-0 flex-col items-center justify-center border-r px-1 text-xs",
                      today && "bg-primary/10 font-bold",
                      weekend && !today && "bg-muted/50 text-muted-foreground",
                      monthBoundary && "border-l-2 border-l-border/70",
                    )}
                    style={{ width: dayColWidth }}
                  >
                    <span className="text-[9px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                      {monthBoundary ? monthShort(dk) : "\u00A0"}
                    </span>
                    <span
                      className={cn(
                        "text-[10px] uppercase",
                        today ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {weekdayShort(dk)}
                    </span>
                    <span
                      className={cn(
                        "text-sm font-semibold",
                        today && "text-primary",
                      )}
                    >
                      {dayOfMonth(dk)}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Lane rows */}
            {LANES.map((lane) => {
              const dayMap = laneData.get(lane.id)!;
              return (
                <div
                  key={lane.id}
                  className={cn("flex border-b", lane.headerBg)}
                  style={{ minHeight: 100 }}
                >
                  {dayKeys.map((dk, index) => {
                    const cellRows = dayMap.get(dk) ?? [];
                    const today = isToday(dk);
                    const weekend = isWeekend(dk);
                    const monthBoundary = isMonthBoundary(dk, dayKeys[index - 1]);
                    return (
                      <div
                        key={dk}
                        data-day-key={dk}
                        data-lane-id={lane.id}
                        className={cn(
                          "flex shrink-0 flex-col gap-1 border-r p-1",
                          today && "bg-primary/5",
                          weekend && !today && "bg-muted/20",
                          monthBoundary && "border-l-2 border-l-border/70",
                        )}
                        style={{ width: dayColWidth }}
                      >
                        {cellRows.length > 0 ? (
                          <TimelineDayGroup lane={lane} rows={cellRows} />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table view columns
// ---------------------------------------------------------------------------

function useColumns(data: CashFlowRow[]) {
  const statusOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of data) {
      set.add(r.isOverdue ? "overdue" : r.status);
    }
    return [...set].sort().map((s) => ({
      label: s === "overdue" ? "Overdue" : s.charAt(0).toUpperCase() + s.slice(1),
      value: s,
    }));
  }, [data]);

  const methodOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of data) {
      if (r.paymentMethod) set.add(r.paymentMethod);
    }
    return [...set].sort().map((m) => ({
      label: PAYMENT_METHOD_LABELS[m] ?? m,
      value: m,
    }));
  }, [data]);

  const phaseOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of data) {
      set.add(r.phase);
    }
    const phaseLabels: Record<string, string> = {
      scheduled: "Scheduled",
      stripe_processing: "Pending Deposit",
      collections: "Collections",
      completed: "Completed",
    };
    return [...set].sort().map((p) => ({
      label: phaseLabels[p] ?? p,
      value: p,
    }));
  }, [data]);

  return React.useMemo<ColumnDef<CashFlowRow>[]>(
    () => [
      {
        accessorKey: "scheduledDate",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Due Date" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-xs font-medium">
              {formatDate(row.original.scheduledDate, {
                month: "short",
                day: "numeric",
                weekday: "short",
              })}
            </span>
            {row.original.isOverdue && (
              <span className="text-[10px] font-medium text-orange-600 dark:text-orange-400">
                Overdue
              </span>
            )}
          </div>
        ),
        meta: { label: "Due Date", variant: "dateRange" as const },
        enableColumnFilter: true,
      },
      {
        accessorKey: "orderNumber",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Order #" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.orderNumber ?? row.original.orderId.slice(-8)}
          </span>
        ),
        meta: { label: "Order #", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        accessorKey: "customerName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Customer" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-xs font-medium">
              {row.original.company || row.original.customerName || "-"}
            </span>
            {row.original.company &&
              row.original.customerName &&
              row.original.company !== row.original.customerName && (
                <span className="text-xs text-muted-foreground">
                  {row.original.customerName}
                </span>
              )}
          </div>
        ),
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const search = String(filterValue).toLowerCase();
          const name = (row.original.customerName ?? "").toLowerCase();
          const company = (row.original.company ?? "").toLowerCase();
          return name.includes(search) || company.includes(search);
        },
        meta: { label: "Customer", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorKey: "isEnterprise",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Type" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getEnterpriseBadgeClass(row.original.isEnterprise)}
          >
            {getEnterpriseBadgeLabel(row.original.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Type",
          variant: "select" as const,
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some(
            (v: string) =>
              v === String(Boolean(row.original.isEnterprise)),
          ),
      },
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Amount" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm font-medium">
            {formatCurrency(row.original.amount)}
          </span>
        ),
        meta: { label: "Amount", variant: "number" as const },
      },
      {
        id: "phase",
        accessorKey: "phase",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Phase" />
        ),
        cell: ({ row }) => {
          const phase = row.original.phase;
          const lane = LANES.find((l) => l.id === phase);
          if (phase === "completed") {
            return (
              <Badge variant="outline" className={STATUS_BADGE_CLASSES.completed}>
                Completed
              </Badge>
            );
          }
          if (!lane) return <span className="text-muted-foreground">-</span>;
          return (
            <div className="flex items-center gap-1.5">
              <div className={cn("h-2 w-2 rounded-full", lane.dotColor)} />
              <span className={cn("text-xs font-medium", lane.colorClass)}>
                {lane.label}
              </span>
            </div>
          );
        },
        meta: {
          label: "Phase",
          variant: "select" as const,
          options: phaseOptions,
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some((v) => v === row.original.phase),
      },
      {
        id: "displayStatus",
        accessorFn: (row) => (row.isOverdue ? "overdue" : row.status),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Status" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getStatusBadgeClass(
              row.original.status,
              row.original.isOverdue,
            )}
          >
            {getStatusLabel(row.original.status, row.original.isOverdue)}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "select" as const,
          options: statusOptions,
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) => {
          const effectiveStatus = row.original.isOverdue
            ? "overdue"
            : row.original.status;
          return filterValues.some((v) => v === effectiveStatus);
        },
      },
      {
        id: "installment",
        accessorFn: (row) =>
          row.installmentNumber && row.totalInstallments
            ? `${row.installmentNumber}/${row.totalInstallments}`
            : null,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Installment" />
        ),
        cell: ({ row }) => {
          const num = row.original.installmentNumber;
          const total = row.original.totalInstallments;
          if (!num || !total) return <span className="text-muted-foreground">-</span>;
          return (
            <Badge
              variant="outline"
              className="border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200"
            >
              {num} of {total}
            </Badge>
          );
        },
        meta: { label: "Installment" },
      },
      {
        accessorKey: "paymentMethod",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Method" />
        ),
        cell: ({ row }) => {
          const method = row.original.paymentMethod;
          if (!method) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-xs">
              {PAYMENT_METHOD_LABELS[method] ?? method}
            </span>
          );
        },
        meta: {
          label: "Method",
          variant: "select" as const,
          options: methodOptions,
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some((v) => v === row.original.paymentMethod),
      },
      {
        id: "estimatedRelease",
        accessorFn: (row) => row.estimatedReleaseDate,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Est. Release" />
        ),
        cell: ({ row }) => {
          const rd = row.original.estimatedReleaseDate;
          const src = row.original.releaseSource;
          if (!rd) return <span className="text-muted-foreground">-</span>;
          return (
            <div className="flex flex-col">
              <span className="text-xs">
                {formatDate(rd, { month: "short", day: "numeric" })}
              </span>
              {src === "estimated" && (
                <span className="text-[10px] text-muted-foreground">est.</span>
              )}
            </div>
          );
        },
        meta: { label: "Est. Release" },
      },
    ],
    [statusOptions, methodOptions, phaseOptions],
  );
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

const csvColumns: ExportColumn[] = [
  {
    key: "scheduledDate",
    label: "Due Date",
    format: (v: number) =>
      v
        ? new Date(v).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
  },
  { key: "orderNumber", label: "Order #" },
  { key: "company", label: "Company" },
  { key: "customerName", label: "Customer" },
  {
    key: "isEnterprise",
    label: "Type",
    format: (v: unknown) => getEnterpriseBadgeLabel(v),
  },
  { key: "amount", label: "Amount", format: formatCurrencyForExport },
  {
    key: "phase",
    label: "Phase",
    format: (v: string) => {
      const labels: Record<string, string> = {
        scheduled: "Scheduled AR Automation",
        stripe_processing: "Pending Deposit",
        collections: "AR in Collections",
        completed: "Completed",
      };
      return labels[v] ?? v;
    },
  },
  {
    key: "status",
    label: "Status",
    format: (v: string) => v.charAt(0).toUpperCase() + v.slice(1),
  },
  {
    key: "installmentNumber",
    label: "Installment",
    format: (v: number | null) => (v != null ? String(v) : ""),
  },
  {
    key: "totalInstallments",
    label: "Total Installments",
    format: (v: number | null) => (v != null ? String(v) : ""),
  },
  {
    key: "paymentMethod",
    label: "Method",
    format: (v: string | null) =>
      v ? (PAYMENT_METHOD_LABELS[v] ?? v) : "",
  },
  {
    key: "estimatedReleaseDate",
    label: "Est. Release",
    format: (v: number | null) =>
      v
        ? new Date(v).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
  },
  {
    key: "stripeStatusMessage",
    label: "Stripe Status",
    format: (v: string | null) => v ?? "",
  },
  {
    key: "failureReason",
    label: "Failure Reason",
    format: (v: string | null) => v ?? "",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ViewMode = "timeline" | "table";

export function CashFlowTab() {
  const [preset, setPreset] = React.useState<RangePreset>("this_month");
  const [viewMode, setViewMode] = React.useState<ViewMode>("timeline");
  const [customRange, setCustomRange] = React.useState<DateRange | undefined>();
  const [isCustomRangeActive, setIsCustomRangeActive] = React.useState(false);
  const [isCustomRangeOpen, setIsCustomRangeOpen] = React.useState(false);
  const [timelineFocusTarget, setTimelineFocusTarget] =
    React.useState<TimelineFocusTarget>("today");

  const customDateRange = React.useMemo(
    () => (customRange ? buildCustomDateRange(customRange) : null),
    [customRange],
  );
  const range = React.useMemo(
    () =>
      isCustomRangeActive && customDateRange
        ? customDateRange
        : getDateRange(preset),
    [customDateRange, isCustomRangeActive, preset],
  );
  const data = useCashflowAR(range.start, range.end);

  const loading = data === undefined;
  const rows = (data?.rows ?? []) as CashFlowRow[];
  const summary = data?.summary;

  const columns = useColumns(rows);

  const selectedLabel = range.label;
  const exportRangeKey = isCustomRangeActive ? "custom" : preset;

  const handlePresetChange = React.useCallback((value: RangePreset) => {
    setPreset(value);
    setIsCustomRangeActive(false);
    setTimelineFocusTarget("today");
  }, []);

  const handleCustomRangeSelect = React.useCallback((nextRange: DateRange | undefined) => {
    setCustomRange(nextRange);
    if (nextRange?.from && nextRange.to) {
      setIsCustomRangeActive(true);
      setTimelineFocusTarget("today");

      // DayPicker treats a first click as a same-day range. Keep the popover
      // open so the user can finish a multi-day selection if they want to.
      if (!isSingleDaySelection(nextRange)) {
        setIsCustomRangeOpen(false);
      }
    }
  }, []);

  const handleShiftTimelineRange = React.useCallback(
    (days: number, focusTarget: TimelineFocusTarget) => {
      setCustomRange(shiftDateSelectionByDays(range, days));
      setIsCustomRangeActive(true);
      setTimelineFocusTarget(focusTarget);
    },
    [range],
  );

  const handleExport = React.useCallback(() => {
    if (rows.length === 0) {
      toast.error("No payments to export");
      return;
    }
    exportToCSV(rows, csvColumns, `cashflow-ar-${exportRangeKey}.csv`);
  }, [exportRangeKey, rows]);

  const handleExportSelected = React.useCallback(
    (selectedRows: CashFlowRow[]) => {
      if (selectedRows.length === 0) {
        toast.error("No payments selected");
        return;
      }
      exportToCSV(
        selectedRows,
        csvColumns,
        `cashflow-ar-selected-${exportRangeKey}.csv`,
      );
    },
    [exportRangeKey],
  );

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={preset}
            onValueChange={(v) => handlePresetChange(v as RangePreset)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="next_month">Next Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="next_30">Next 30 Days</SelectItem>
              <SelectItem value="next_60">Next 60 Days</SelectItem>
              <SelectItem value="next_90">Next 90 Days</SelectItem>
            </SelectContent>
          </Select>

          <Popover open={isCustomRangeOpen} onOpenChange={setIsCustomRangeOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-[240px] justify-start text-left font-normal",
                  isCustomRangeActive && "border-primary/40 bg-primary/5 text-foreground",
                  !customRange?.from && "text-muted-foreground",
                )}
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                {formatDateRangeTriggerLabel(customRange)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                numberOfMonths={2}
                captionLayout="dropdown"
                selected={customRange}
                defaultMonth={customRange?.from ?? new Date(range.start)}
                onSelect={handleCustomRangeSelect}
              />
              <div className="flex items-center justify-between border-t px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  Pick a start and end date to apply a custom range.
                </span>
                <div className="flex items-center gap-2">
                  {customRange?.from || customRange?.to ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCustomRange(undefined);
                        setIsCustomRangeActive(false);
                        setTimelineFocusTarget("today");
                      }}
                    >
                      Clear
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsCustomRangeOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* View mode toggle */}
          <div className="inline-flex items-center rounded-lg bg-muted p-[3px]">
            <button
              type="button"
              onClick={() => setViewMode("timeline")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-all",
                viewMode === "timeline"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Timeline
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-all",
                viewMode === "table"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Table2 className="h-3.5 w-3.5" />
              Table
            </button>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Export All
        </Button>
      </div>

      {/* Contextual badges */}
      {!loading && rows.length > 0 && summary && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="border-border/80 bg-muted/30 text-foreground"
          >
            {selectedLabel}
          </Badge>
          <Badge
            variant="outline"
            className="border-border/80 bg-muted/30 text-foreground"
          >
            {summary.totalPayments.toLocaleString()} payments
          </Badge>
          {summary.pendingCount > 0 && (
            <Badge
              variant="outline"
              className={STATUS_BADGE_CLASSES.pending}
            >
              {summary.pendingCount.toLocaleString()} scheduled
            </Badge>
          )}
          {summary.processingCount > 0 && (
            <Badge
              variant="outline"
              className={STATUS_BADGE_CLASSES.processing}
            >
              {summary.processingCount.toLocaleString()} pending deposit
            </Badge>
          )}
          {summary.completedCount > 0 && (
            <Badge
              variant="outline"
              className={STATUS_BADGE_CLASSES.completed}
            >
              {summary.completedCount.toLocaleString()} collected
            </Badge>
          )}
          {summary.failedCount > 0 && (
            <Badge variant="outline" className={STATUS_BADGE_CLASSES.failed}>
              {summary.failedCount.toLocaleString()} failed
            </Badge>
          )}
          {summary.overdueCount > 0 && (
            <Badge variant="outline" className={OVERDUE_BADGE}>
              {summary.overdueCount.toLocaleString()} overdue
            </Badge>
          )}
        </div>
      )}

      {/* Summary metric cards */}
      {!loading && rows.length > 0 && summary && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <InternalMetricCard
            title="Total Expected"
            value={formatCurrency(summary.totalExpected)}
            hint={selectedLabel}
            icon={DollarSign}
          />
          <InternalMetricCard
            title="Scheduled"
            value={formatCurrency(summary.totalPending)}
            hint={`${summary.pendingCount} payment${summary.pendingCount !== 1 ? "s" : ""}`}
            icon={Clock}
          />
          <InternalMetricCard
            title="Pending Deposit"
            value={formatCurrency(summary.totalProcessing)}
            hint={`${summary.processingCount} payment${summary.processingCount !== 1 ? "s" : ""}`}
            icon={Loader2}
          />
          <InternalMetricCard
            title="Collected"
            value={formatCurrency(summary.totalCompleted)}
            hint={`${summary.completedCount} payment${summary.completedCount !== 1 ? "s" : ""}`}
            icon={CheckCircle2}
          />
          <InternalMetricCard
            title="Collections"
            value={formatCurrency(summary.totalFailed + summary.overdueAmount)}
            hint={`${summary.failedCount + summary.overdueCount} item${summary.failedCount + summary.overdueCount !== 1 ? "s" : ""}`}
            icon={AlertTriangle}
          />
          <InternalMetricCard
            title="Collection Rate"
            value={
              summary.totalPayments > 0
                ? `${((summary.completedCount / summary.totalPayments) * 100).toFixed(1)}%`
                : "-"
            }
            hint={selectedLabel}
            icon={TrendingUp}
          />
        </div>
      )}

      {/* Views */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center rounded-md border border-dashed">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <CalendarDays className="h-8 w-8 opacity-40" />
            <p className="text-sm font-medium">No scheduled payments</p>
            <p className="text-xs">
              No payment collections scheduled for {selectedLabel}
            </p>
          </div>
        </div>
      ) : viewMode === "timeline" ? (
        <TimelineView
          rows={rows}
          startMs={range.start}
          endMs={range.end}
          focusTarget={timelineFocusTarget}
          onShiftRange={handleShiftTimelineRange}
        />
      ) : (
        <InternalEntityDataTable
          data={rows}
          columns={columns}
          pageSize={25}
          actionBar={({ selectedRows, clearSelection }) => (
            <DataTableFloatingBar
              selectedCount={selectedRows.length}
              onClear={clearSelection}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleExportSelected(selectedRows)}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Export Selected
              </Button>
            </DataTableFloatingBar>
          )}
        />
      )}
    </div>
  );
}
